/**
 * EntityThread — Wave 5.9 Build 10 (2026-02).
 *
 * Generic reusable comment thread with photo attachments, backed by
 *   /api/threads/{entity_type}/{entity_id}                (internal)
 *   /api/contractor-portal/threads/{entity_type}/{entity_id}   (contractor)
 *
 * Build 10 changes:
 *   - Photo cap raised from 5 → 10 per comment
 *   - Camera + Library buttons visible in the composer (mirrors
 *     PhotoCaptureField two-button pattern; not hidden behind an
 *     "Attach" icon anymore).
 *   - Each rendered photo shows uploader name + timestamp, and a
 *     Delete affordance when allowed (poster or designer, until verify).
 *   - Photos delete via DELETE
 *       /api/threads/{entity_type}/{entity_id}/messages/{mid}/photos/{pid}
 *     (contractor path uses /api/contractor-portal/threads/...).
 *
 * Props:
 *   entityType    string   e.g. 'punch_item'
 *   entityId      string   the entity's id
 *   party         'internal' | 'contractor'
 *   axiosInstance axios    axios instance (already carries auth headers)
 *   canPost       bool     if false, thread is read-only
 *   canDeletePhotos bool   default true; false disables all delete affordances
 *   currentUserId string   used to gate "delete own photo"; contractor mode
 *                          derives this from the session backend
 *   isDesigner    bool     when true, delete affordance always visible
 *   entityVerified bool    when true, delete affordances hidden per policy
 *   maxPhotos     number   default 10
 *   darkMode      bool     observed if omitted
 *   emptyLabel    string
 *   testIdPrefix  string
 */
import { useEffect, useRef, useState, useMemo } from 'react';
import { Send, X, Loader2, MessageSquare, Camera, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { PUNCH_BTN_PRIMARY, PUNCH_BTN_SECONDARY } from '../utils/punchButtons';
import { toast } from 'sonner';

const DEFAULT_MAX_PHOTOS = 10;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

export default function EntityThread({
  entityType,
  entityId,
  party = 'internal',
  axiosInstance,
  canPost = true,
  canDeletePhotos = true,
  currentUserId,
  isDesigner = false,
  entityVerified = false,
  maxPhotos = DEFAULT_MAX_PHOTOS,
  darkMode: darkModeProp,
  emptyLabel = 'No messages yet.',
  testIdPrefix = 'entity-thread',
  compact = false,        // TK-7 — tighter, token-styled rows for the task panel.
  placeholder,            // TK-7 — the composer's own words.
  mentionUsers = [],      // TK-7 — [{id, name}] offered after an "@".
  onPosted,               // TK-7 — called with the new message after a post.
  extraFields,            // TK-7b — extra form fields to send with a post.
  initialMessages,  // Wave 5.9 Build 11 — skips per-mount fetch when parent batched.
}) {
  const darkMode = useMemo(() => {
    if (typeof darkModeProp === 'boolean') return darkModeProp;
    if (typeof document !== 'undefined') return document.documentElement.classList.contains('dark');
    return false;
  }, [darkModeProp]);

  const [messages, setMessages] = useState(Array.isArray(initialMessages) ? initialMessages : []);
  const [loading, setLoading] = useState(!Array.isArray(initialMessages));
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState({});
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const textRef = useRef(null);

  const basePath = party === 'contractor'
    ? `${API}/contractor-portal/threads/${entityType}/${entityId}`
    : `${API}/threads/${entityType}/${entityId}`;

  const load = async () => {
    if (!entityId || !axiosInstance) return;
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(basePath);
      setMessages(data?.messages || []);
    } catch (e) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(initialMessages)) {
      setMessages(initialMessages);
      setLoading(false);
      return;
    }
    load();
  }, [entityId, party, initialMessages]);

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const merged = [...files, ...picked].slice(0, maxPhotos);
    if (files.length + picked.length > maxPhotos) {
      toast.error(`Max ${maxPhotos} photos per comment`);
    }
    setFiles(merged);
    previews.forEach((p) => URL.revokeObjectURL(p));
    setPreviews(merged.map((f) => URL.createObjectURL(f)));
    e.target.value = '';
  };

  const removeFile = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    previews.forEach((p) => URL.revokeObjectURL(p));
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const submit = async () => {
    if (posting) return;
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) {
      toast.error('Add a message or a photo first');
      return;
    }
    if (files.length > maxPhotos) {
      toast.error(`Max ${maxPhotos} photos per comment`);
      return;
    }
    setPosting(true);
    try {
      const fd = new FormData();
      Object.entries(extraFields || {}).forEach(([k, v]) => fd.append(k, v));
      fd.append('text', trimmed);
      files.forEach((f) => fd.append('photos', f));
      const { data } = await axiosInstance.post(`${basePath}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages((prev) => [...prev, data]);
      setText('');
      onPosted?.(data);
      previews.forEach((p) => URL.revokeObjectURL(p));
      setFiles([]);
      setPreviews([]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to post message';
      toast.error(typeof detail === 'string' ? detail : 'Failed to post message');
    } finally {
      setPosting(false);
    }
  };

  const canDeleteThisPhoto = (photo) => {
    if (!canDeletePhotos) return false;
    if (entityVerified) return false;
    if (isDesigner) return true;
    if (!currentUserId) return false;
    return photo.uploaded_by === currentUserId;
  };

  const deletePhoto = async (message, photo) => {
    const key = `${message.id}:${photo.id}`;
    if (deleting[key]) return;
    if (!window.confirm('Delete this photo? An audit entry will be appended to the timeline.')) return;
    setDeleting((d) => ({ ...d, [key]: true }));
    try {
      await axiosInstance.delete(`${basePath}/messages/${message.id}/photos/${photo.id}`);
      setMessages((prev) => prev.map((m) => (
        m.id === message.id
          ? { ...m, photos: (m.photos || []).filter((p) => p.id !== photo.id) }
          : m
      )));
      toast.success('Photo removed');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not delete photo';
      toast.error(typeof detail === 'string' ? detail : 'Could not delete photo');
    } finally {
      setDeleting((d) => { const next = { ...d }; delete next[key]; return next; });
    }
  };

  // TK-7 — "@" opens the firm's list, filtered by what follows it.
  const mentionQuery = useMemo(() => {
    if (!mentionUsers.length) return null;
    const m = /@([^\s@]*)$/.exec(text);
    return m ? m[1] : null;
  }, [text, mentionUsers.length]);
  const mentionHits = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return mentionUsers.filter((u) => !q || String(u.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, mentionUsers]);
  const insertMention = (name) => {
    setText((prev) => prev.replace(/@([^\s@]*)$/, `@${name} `));
    textRef.current?.focus();
  };

  const bubbleCls = (msg) => {
    const isMine = party === msg.party || (party === 'internal' && msg.party === 'internal');
    const light = isMine ? 'bg-blue-50 border-blue-100' : 'bg-slate-50 border-slate-200';
    const dark = isMine ? 'bg-slate-800/60 border-slate-700' : 'bg-[#0F0F0F] border-[#2A2A2A]';
    return darkMode ? dark : light;
  };

  return (
    <div className={compact ? 'kmt-thread' : 'space-y-3'} data-testid={testIdPrefix}>
      {!compact && (
        <div className={`text-xs font-medium flex items-center gap-1 ${darkMode ? 'text-slate-300' : 'text-slate-500'}`}>
          <MessageSquare className="w-3.5 h-3.5" />
          Comments {messages.length > 0 ? `(${messages.length})` : ''}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs opacity-70">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : messages.length === 0 ? (
        <div className={`text-xs italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{emptyLabel}</div>
      ) : (
        <div className={compact ? 'kmt-thread__msgs' : 'space-y-2 max-h-72 overflow-y-auto pr-1'}>
          {messages.map((m) => (compact ? (
            <div key={m.id} className={`msg${m.author_id === currentUserId ? ' me' : ''}`}
              data-testid={`${testIdPrefix}-msg-${m.id}`}>
              <div className="who">
                <b>{m.author_name || 'User'}{m.party === 'contractor' ? ' · Site Office' : ''}</b>
                <i>{m.to_party && m.party_name ? `→ ${m.party_name}` : fmt(m.created_at)}</i>
              </div>
              {m.text ? <div className="txt">{m.text}</div> : null}
              {(m.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {m.photos.map((p) => (
                    <a key={p.id || p.url} href={p.url} target="_blank" rel="noreferrer"
                      className="block w-16 h-16 overflow-hidden"
                      data-testid={`${testIdPrefix}-photo-${p.id || p.url}`}>
                      <img src={p.url} alt={p.filename || 'attachment'} className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              key={m.id}
              className={`rounded-md border px-3 py-2 ${bubbleCls(m)}`}
              data-testid={`${testIdPrefix}-msg-${m.id}`}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-xs font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-700'}`}>
                  {m.author_name || 'User'}
                </span>
                <span className={`text-[10px] uppercase tracking-wide rounded px-1 py-0.5 ${
                  m.party === 'contractor'
                    ? (darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700')
                    : (darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700')
                }`}>
                  {m.party}
                </span>
                <span className={`ml-auto text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {fmt(m.created_at)}
                </span>
              </div>
              {m.text ? (
                <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{m.text}</p>
              ) : null}
              {(m.photos || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.photos.map((p) => {
                    const delKey = `${m.id}:${p.id}`;
                    const showDelete = canDeleteThisPhoto(p);
                    return (
                      <div
                        key={p.id || p.url}
                        className="relative group"
                        data-testid={`${testIdPrefix}-photo-${p.id || p.url}`}
                      >
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-20 h-20 rounded overflow-hidden border border-slate-300 dark:border-slate-700"
                          title={`${p.uploaded_by_name || 'Unknown'} · ${fmt(p.uploaded_at)}`}
                        >
                          <img src={p.url} alt={p.filename || 'attachment'} className="w-full h-full object-cover" loading="lazy" />
                        </a>
                        <div className={`mt-1 text-[9px] leading-tight ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <div className="truncate max-w-[80px]">{p.uploaded_by_name || 'Unknown'}</div>
                          <div>{fmt(p.uploaded_at)}</div>
                        </div>
                        {showDelete && (
                          <button
                            type="button"
                            onClick={() => deletePhoto(m, p)}
                            disabled={!!deleting[delKey]}
                            className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                            title="Delete photo"
                            data-testid={`${testIdPrefix}-photo-delete-${p.id}`}
                          >
                            {deleting[delKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )))}
        </div>
      )}

      {canPost && (
        <div className={compact ? 'kmt-thread__composer' : `rounded-md border p-2 ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-slate-200 bg-white'}`}>
          {mentionHits.length > 0 && (
            <div className="kmt-menu" data-testid={`${testIdPrefix}-mentions`}>
              {mentionHits.map((u) => (
                <button type="button" key={u.id} onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertMention(u.name)}
                  data-testid={`${testIdPrefix}-mention-${u.id}`}>{u.name}</button>
              ))}
            </div>
          )}
          <Textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder || `Add a comment (attach up to ${maxPhotos} photos)…`}
            rows={2}
            className={`text-sm resize-none ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-slate-100 placeholder:text-slate-500' : ''}`}
            data-testid={`${testIdPrefix}-input`}
          />
          {previews.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {previews.map((src, idx) => (
                <div key={idx} className="relative w-14 h-14 rounded overflow-hidden border border-slate-300 dark:border-slate-700">
                  <img src={src} alt="preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="absolute top-0 right-0 bg-black/60 text-white rounded-bl px-1 text-xs"
                    aria-label="Remove"
                    data-testid={`${testIdPrefix}-remove-preview-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Camera + Library buttons — both always visible, no icon-only affordance. */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={posting || files.length >= maxPhotos}
              className={PUNCH_BTN_SECONDARY}
              data-testid={`${testIdPrefix}-take-btn`}
            >
              <Camera className="w-4 h-4" />
              Take photo
            </button>
            <button
              type="button"
              onClick={() => libraryRef.current?.click()}
              disabled={posting || files.length >= maxPhotos}
              className={PUNCH_BTN_SECONDARY}
              data-testid={`${testIdPrefix}-library-btn`}
            >
              <ImageIcon className="w-4 h-4" />
              Choose existing
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {files.length}/{maxPhotos} photos
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={submit}
              disabled={posting || (!text.trim() && files.length === 0)}
              className={PUNCH_BTN_PRIMARY}
              data-testid={`${testIdPrefix}-send-btn`}
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Post
            </button>
          </div>

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={handleFilePick}
            data-testid={`${testIdPrefix}-camera-input`}
          />
          <input
            ref={libraryRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFilePick}
            data-testid={`${testIdPrefix}-library-input`}
          />
        </div>
      )}
    </div>
  );
}
